import React from "react";
import { CircularProgress, Menu } from "@mui/material";
import BaseTooltip from "@reusables/BaseTooltip";
import { BaseTinyActionIcon, TinyActionIconType } from "@components/reusables/BaseTinyActionIcon";
import { useHistory } from "react-router-dom";
import { useAbility } from "@casl/react";
import { AbilityContext } from "@/casl.config";
import { useTranslation } from "react-i18next";
import { ReactComponent as FastCartSVG } from "@assets/icons/ic_fast_cart.svg";
import BasePermissionBlocker from "@reusables/BasePermissionBlocker";
import { useAppDispatch } from "@/redux/hooks";
import { salesOrdersApi } from "@/redux/features/sales/orders/salesOrdersApi";
import { handleSaleOrderCopy } from "@redux/features/sales/orders/thunks";
import { BadgeStatus, SaleOrder } from "@/types/general";
import {
    CancelOrderAction,
    isOrderPickingDisabled,
    isPrepaidAwaitingInvoice,
} from "@components/Dashboard/pages/Sales/Orders/utils";
import { toastError } from "@helpers/utils";

export interface ActionsMenuProps {
    anchorEl?: HTMLElement;
    open: boolean;
    onClose: () => void;
    actionEntity?: SaleOrder.Root;
    onCreatePO: (saleOrder: SaleOrder.Root) => Promise<void>;
    onOpenDeleteModal: () => void;
    onOpenCancelModal: () => void;
}

export default function ActionsMenu(props: ActionsMenuProps) {
    const {
        anchorEl,
        open,
        onClose,
        actionEntity,
        onCreatePO,
        onOpenDeleteModal,
        onOpenCancelModal,
    } = props;
    const history = useHistory();
    const ability = useAbility(AbilityContext);
    const { t } = useTranslation("", { keyPrefix: "sales.orders.main" });
    const dispatch = useAppDispatch();

    const [isStartPickingLoading, setIsStartPickingLoading] = React.useState(false);

    const disableAllActions = isStartPickingLoading;
    const isOfferStatus = actionEntity?.status.name === BadgeStatus.Base.offer;
    const isPrepaidInvoicePending = isPrepaidAwaitingInvoice(actionEntity);
    const isPickingDisabled = isOrderPickingDisabled(actionEntity);
    const pickingTooltip = isOfferStatus
        ? `${t("actions.pickingNotAllowed.offer")}`
        : isPrepaidInvoicePending
        ? `${t("actions.pickingNotAllowed.prepaid")}`
        : `${t("actions.picking")}`;

    const handleStartPicking = async () => {
        if (
            !actionEntity ||
            !ability.can("create", "picking") ||
            isStartPickingLoading ||
            isPickingDisabled
        )
            return;
        try {
            setIsStartPickingLoading(true);
            const extended = await dispatch(
                salesOrdersApi.endpoints.getModuleSaleOrder.initiate(
                    {
                        module: "picking",
                        id: actionEntity.id,
                    },
                    { subscribe: false }
                )
            ).unwrap();

            history.push({ pathname: `/dashboard/picking/new`, state: { saleOrder: extended } });
            onClose();
        } catch (e) {
            toastError(e);
        } finally {
            setIsStartPickingLoading(false);
        }
    };

    return (
        <Menu id="sales-orders-actions-menu" anchorEl={anchorEl} open={open} onClose={onClose}>
            <div
                className="flex space-x-[16px] p-[18px] bg-gradient-to-r from-gradients-primaryLinear-from to-gradients-primaryLinear-to text-[#fff]"
                style={{
                    pointerEvents: disableAllActions ? "none" : undefined,
                    opacity: disableAllActions ? 0.6 : 1,
                }}
            >
                <BaseTooltip title={t("actions.details") || ""} placement="bottom">
                    <BaseTinyActionIcon
                        type={TinyActionIconType.ViewDetails}
                        onClick={() =>
                            actionEntity &&
                            history.push(`/dashboard/sales/orders/${actionEntity.id}/details`)
                        }
                        variant="gradient"
                    />
                </BaseTooltip>

                <BasePermissionBlocker action="create" module="sale_order">
                    <BaseTooltip
                        active={ability.can("create", "sale_order")}
                        title={t("actions.duplicate") || ""}
                        placement="bottom"
                    >
                        <BaseTinyActionIcon
                            type={TinyActionIconType.Duplicate}
                            onClick={() => {
                                if (!actionEntity) return;

                                void dispatch(handleSaleOrderCopy(actionEntity.id, history)).then(
                                    (wasCopied) => {
                                        if (wasCopied) {
                                            onClose();
                                        }
                                    }
                                );
                            }}
                            variant="gradient"
                        />
                    </BaseTooltip>
                </BasePermissionBlocker>

                <BasePermissionBlocker action="create" module="picking">
                    <BaseTooltip active title={pickingTooltip} placement="bottom">
                        <div className="relative">
                            <BaseTinyActionIcon
                                type={TinyActionIconType.StartPicking}
                                onClick={handleStartPicking}
                                variant="gradient"
                                className={
                                    isPickingDisabled
                                        ? "opacity-20 pointer-events-none"
                                        : isStartPickingLoading
                                        ? "opacity-50"
                                        : ""
                                }
                            />
                            {isStartPickingLoading && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <CircularProgress size={18} color="inherit" />
                                </div>
                            )}
                        </div>
                    </BaseTooltip>
                </BasePermissionBlocker>

                <BasePermissionBlocker action="purchase_order.create" module="sale_order">
                    <BaseTooltip
                        active={ability.can("purchase_order.create", "sale_order")}
                        title={t("actions.createPO") + ""}
                        placement="bottom"
                    >
                        <FastCartSVG
                            onClick={async () => {
                                const order = actionEntity;
                                if (order && ability.can("purchase_order.create", "sale_order")) {
                                    await onCreatePO(order);
                                }
                                onClose();
                            }}
                            data-testid="sales-orders-action-create-po"
                        />
                    </BaseTooltip>
                </BasePermissionBlocker>

                <BasePermissionBlocker action="edit" module="sale_order">
                    <BaseTooltip
                        active={ability.can("edit", "sale_order")}
                        title={t("actions.edit") || ""}
                        placement="bottom"
                        className={actionEntity?.is_editable ? "" : "cursor-not-allowed"}
                    >
                        <BaseTinyActionIcon
                            type={TinyActionIconType.Edit}
                            onClick={() =>
                                ability.can("edit", "sale_order") &&
                                actionEntity &&
                                history.push(`/dashboard/sales/orders/${actionEntity.id}/edit`)
                            }
                            variant="gradient"
                            className={
                                actionEntity?.is_editable ? "" : "opacity-20 pointer-events-none"
                            }
                        />
                    </BaseTooltip>
                </BasePermissionBlocker>

                <BasePermissionBlocker action="delete" module="sale_order">
                    <BaseTooltip
                        title={t("actions.delete") || ""}
                        placement="bottom"
                        className={actionEntity?.is_editable ? "" : "cursor-not-allowed"}
                    >
                        <BaseTinyActionIcon
                            type={TinyActionIconType.Delete}
                            onClick={() => {
                                if (ability.can("delete", "sale_order")) {
                                    onOpenDeleteModal();
                                    onClose();
                                }
                            }}
                            variant="gradient"
                            className={
                                actionEntity?.is_editable ? "" : "opacity-20  pointer-events-none"
                            }
                        />
                    </BaseTooltip>
                </BasePermissionBlocker>

                <BasePermissionBlocker action="cancel" module="sale_order">
                    <CancelOrderAction
                        actionEntity={actionEntity}
                        action="cancel"
                        module="sale_order"
                        t={t}
                        onClick={() => {
                            onOpenCancelModal();
                            onClose();
                        }}
                    />
                </BasePermissionBlocker>
            </div>
        </Menu>
    );
}
